import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import styled from "styled-components";
import {
  InternalReportStatusEnum,
  Report,
  ReportStatus,
  ReportType,
} from "../api/models/report.models";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import moment from "moment";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useEffect, useMemo, useState } from "react";
import { useMonthName } from "../utils/monthUtils";
import { StatusTag } from "../components/statusTag";
import { Company, Organization } from "../api/models/companies.models";
import { confirmDialog } from "primereact/confirmdialog";
import useDeleteReport from "../hooks/useDeleteReport";
import CommentDialog from "../dialogs/commentDialog";
import AdminReportReviewDialog from "../dialogs/adminReportReviewDialog";
import { InfoDot } from "../components/infoDot";
import { Tooltip } from "primereact/tooltip";
import DownloadPdfButton from "../components/downloadPdfButton";
import useEcomodClasses from "../hooks/useEcomodClasses";
import useSupClasses from "../hooks/useSupClasses";
import usePrices from "../hooks/usePrices";
import { createSummaryData, getMatchingReport } from "../utils/summaryUtils";
import { getFormalLocale } from "../utils/appUtils";
import useUpdateReportStatus from "../hooks/useUpdateReportStatus";

const StyledTable = styled(DataTable)`
  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 1.5rem 1rem;

    @media (max-width: 1400px) {
      font-size: 0.9rem;
    }
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0;
      border: none;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      border: none;
    }
  }
`;

const StyledLink = styled.a`
  color: var(--sumi-blue) !important;
  text-decoration: underline !important;
  cursor: pointer;
`;

const ReportRowHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ReportRowHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-right: 117px;
`;

const DeleteIcon = styled.i`
  color: #333;
  cursor: pointer;
  border-radius: 4px;
  padding: 0.2rem;

  &:focus {
    outline: 2px solid rgba(128, 128, 128, 0.5);
    outline-offset: 2px;
    box-shadow: 0 0 4px rgba(128, 128, 128, 0.3);
  }
`;

const ChatIconContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const ChatIcon = styled.i`
  color: var(--sumi-blue);
  cursor: pointer;
  border-radius: 4px;
  padding: 0.4rem;
  margin-right: 10px;
  font-size: 1.2rem;

  &:focus {
    outline: 2px solid rgba(128, 128, 128, 0.5);
    outline-offset: 2px;
    box-shadow: 0 0 4px rgba(128, 128, 128, 0.3);
  }
`;

const MessageCountBadge = styled.span`
  position: absolute;
  top: 0;
  right: 8px;
  background-color: var(--sumi-error-color, #dc3545);
  color: white;
  border-radius: 50%;
  min-width: 16px;
  height: 16px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const ReturnIcon = styled.i`
  color: var(--sumi-blue);
  cursor: pointer;
  border-radius: 4px;
  padding: 0.4rem;
  margin-right: 10px;
  font-size: 1.2rem;
  transform: rotate(180deg);

  &:focus {
    outline: 2px solid rgba(128, 128, 128, 0.5);
    outline-offset: 2px;
    box-shadow: 0 0 4px rgba(128, 128, 128, 0.3);
  }
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  justify-content: flex-end;
`;

const FlagIcon = styled.i<{ $status?: InternalReportStatusEnum }>`
  color: ${(props) =>
    props.$status === InternalReportStatusEnum.ReturnedForCorrection
      ? "var(--sumi-warning-color)"
      : props.$status === InternalReportStatusEnum.ApprovedAutomatically ||
          props.$status === InternalReportStatusEnum.ApprovedManually
        ? "var(--sumi-light-green)"
        : "var(--sumi-error-color)"};
  cursor: pointer;
  border-radius: 4px;
  padding: 0.4rem;
  margin-right: 10px;
  font-size: 1.2rem;

  &:focus {
    outline: 2px solid rgba(128, 128, 128, 0.5);
    outline-offset: 2px;
    box-shadow: 0 0 4px rgba(128, 128, 128, 0.3);
  }
`;

interface ReportsTableProps {
  reports: (Report & { organizationName?: string })[];
  adminMode?: boolean;
  organizations?: Organization[];
  returnToEditReport?: (reportId: number) => void;
  company?: Company;
  handleSelectCombinedViewReport?: (report: Report) => void;
  groupRowsBy: string;
  allReports?: Report[];
  showPagination?: boolean;
  rows?: number;
  setPage?: (page: number) => void;
  setRows?: (rows: number) => void;
  totalReports?: number;
  page?: number;
  showOrganizationName?: boolean;
  showCompanyName?: boolean;
  showTotalWeight?: boolean;
  showReportType?: boolean;
}

const ReportsTable: React.FC<ReportsTableProps> = ({
  reports,
  adminMode,
  handleSelectCombinedViewReport,
  organizations,
  returnToEditReport,
  company,
  allReports,
  showPagination = false,
  setPage,
  setRows,
  totalReports,
  rows = 10,
  page = 1,
  groupRowsBy,
  showOrganizationName = false,
  showCompanyName = false,
  showTotalWeight = false,
  showReportType = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reportChatDialog, setReportChatDialog] = useState<number>(0);
  const [reportReviewDialog, setReportReviewDialog] = useState<{
    report?: Report;
  } | null>(null);

  const [companyId, organizationId, locale] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.organizationId,
      state.locale,
    ]),
  );
  const formalLocale = getFormalLocale(locale);
  const [tableData, setTableData] = useState<Report[]>([]);
  const [isCombinedOrganizationData, setIsCombinedOrganizationData] =
    useState<boolean>(false);

  // Stable empty arrays to prevent infinite re-renders in React PDF
  const emptyArray = useMemo(() => [], []);

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();
  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();
  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: 2025, // future proofing, move pdf wrapper to its own component later
  });

  useEffect(() => {
    if (reports?.every((r) => !r.organizationName)) {
      setIsCombinedOrganizationData(false);
      setTableData(reports);
    } else {
      setTableData(reports);
      setIsCombinedOrganizationData(true);
    }
  }, [reports]);

  const { getMonthName } = useMonthName();

  const { deleteReport, isDeleting } = useDeleteReport({
    companyId: companyId as number,
  });

  const { returnReportForEditing, isUpdating } = useUpdateReportStatus({
    companyId: companyId as number,
  });

  const quarterTemplate = (rowData: Report) => {
    if (rowData.quarter) {
      return <span>{`Q${rowData.quarter ?? ""}`}</span>;
    }

    if (rowData.month) {
      return <span>{getMonthName(rowData.month ?? 1)}</span>;
    }

    return <span></span>;
  };

  const getStatusText = (status: ReportStatus | undefined) => {
    switch (status) {
      case ReportStatus.Draft:
        return t("label-draft");
      case ReportStatus.Sent:
        return t("label-sent");
      case ReportStatus.WaitingForApproval:
        return t("label-waiting-for-approval");
      default:
        return t("label-no-status");
    }
  };

  const getInternalStatusText = (status?: InternalReportStatusEnum) => {
    switch (status) {
      case InternalReportStatusEnum.ToBeReviewed:
        return t("label-to-be-reviewed");
      case InternalReportStatusEnum.ReturnedForCorrection:
        return t("label-returned-for-correction");
      case InternalReportStatusEnum.ApprovedAutomatically:
        return t("label-approved-automatically");
      case InternalReportStatusEnum.ApprovedManually:
        return t("label-approved-manually");
      default:
        return "";
    }
  };

  const statusbody = (rowData: Report) => {
    // report unfinished
    if (rowData.reportStatus === 1) {
      return (
        <InfoDot
          position="right"
          text={t("infotext-report-draft")}
          marginLeft="10px"
        >
          <StatusTag type={rowData.reportStatus || null}>
            {getStatusText(rowData.reportStatus)}
          </StatusTag>
        </InfoDot>
      );
    }

    // report waiting for sending
    if (rowData.reportStatus === 3) {
      return (
        <InfoDot
          position="right"
          text={t("infotext-report-waiting")}
          marginLeft="10px"
        >
          <StatusTag type={rowData.reportStatus || null}>
            {getStatusText(rowData.reportStatus)}
          </StatusTag>
        </InfoDot>
      );
    }
    return (
      <StatusTag type={rowData.reportStatus || null}>
        {getStatusText(rowData.reportStatus)}
      </StatusTag>
    );
  };

  // link styled item to download report
  const downloadReportTemplate = (rowData: Report) => {
    // no content if report is not created
    if (!rowData.reportStatus) {
      return <span></span>;
    }

    const prevYearReports = allReports?.filter(
      (report) =>
        report.year === rowData.year - 1 &&
        (report.reportStatus === ReportStatus.Sent ||
          report.reportStatus === ReportStatus.WaitingForApproval) &&
        report.id !== rowData.id,
    );

    const prevYearReport = getMatchingReport(prevYearReports, rowData);

    const { consumer, business, sup } = createSummaryData(
      rowData,
      ecomodClasses || emptyArray,
      supClasses || emptyArray,
      pricesData || emptyArray,
      locale,
    );

    const totalCost = [...consumer, ...business, ...sup]
      .reduce((sum, row) => sum + (row.price || 0), 0)
      .toFixed(2);

    const {
      consumer: consumerPrevYear,
      business: businessPrevYear,
      sup: supPrevYear,
    } = prevYearReport
      ? createSummaryData(
          prevYearReport,
          ecomodClasses || emptyArray,
          supClasses || emptyArray,
          pricesData || emptyArray,
          locale,
        )
      : { consumer: [], business: [], sup: [] };

    const rowOrganizationId = rowData.organizationId;

    return (
      <div>
        <DownloadPdfButton
          reportData={rowData}
          ecomodClasses={ecomodClasses || emptyArray}
          supClasses={supClasses || emptyArray}
          pricesData={pricesData || emptyArray}
          reportCompany={company || null}
          reportOrganization={
            rowOrganizationId
              ? company?.organizations?.find(
                  (org) => org.id === rowOrganizationId,
                )
              : undefined
          }
          consumer={consumer}
          business={business}
          sup={sup}
          consumerPrevYear={consumerPrevYear}
          businessPrevYear={businessPrevYear}
          supPrevYear={supPrevYear}
          prevYearReport={prevYearReport}
          totalCost={totalCost}
        />
      </div>
    );
  };

  const viewReportTemplate = (rowData: Report) => {
    // Main level company cannot edit organization's draft reports
    if (
      rowData?.reportStatus === ReportStatus.Draft &&
      rowData.organizationId !== organizationId &&
      !adminMode
    ) {
      return (
        <StyledLink
          tabIndex={0}
          onClick={(e) => handleViewReportClick(e, rowData.id)}
        >
          {t("cmd-view-report")}
        </StyledLink>
      );
    }

    // admin can only view report
    if (adminMode) {
      return (
        <StyledLink
          tabIndex={0}
          onClick={(e) => handleViewReportClick(e, rowData.id)}
        >
          {t("cmd-view-report")}
        </StyledLink>
      );
    }

    // create report link if report is not created
    if (rowData.reportStatus === undefined) {
      return (
        <StyledLink
          tabIndex={0}
          onClick={handleNewReportClick}
          className="create-report"
        >
          {t("cmd-create-report")}
        </StyledLink>
      );
    }

    if (
      rowData.reportStatus === ReportStatus.Sent ||
      rowData.reportStatus === ReportStatus.WaitingForApproval
    ) {
      return (
        <StyledLink
          tabIndex={0}
          onClick={(e) => handleViewReportClick(e, rowData.id)}
        >
          {t("cmd-view-report")}
        </StyledLink>
      );
    }

    return (
      <StyledLink
        tabIndex={0}
        onClick={(e) => handleNavigateToReportClick(e, rowData)}
        className="view-report"
      >
        {t("cmd-continue-working-on-report")}
      </StyledLink>
    );
  };

  const handleNewReportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // navigate to report page, query params with year
    navigate(
      `/reporting-choice&companyId=${companyId}?organizationId=${organizationId}`,
    );
  };

  const handleNavigateToReportClick = (e, rowData: Report) => {
    e.preventDefault();
    e.stopPropagation();

    if (!rowData.id) {
      return;
    }

    switch (rowData.reportType) {
      case ReportType.EAN:
        navigate(`/report/${rowData.id}`);
        break;
      case ReportType.DETAILED:
        navigate(`/detailed-report/${rowData.id}`);
        break;
      case ReportType.LIGHT:
        navigate(`/light-report/${rowData.id}`);
        break;
      default:
        navigate(`/report/${rowData.id}`);
        break;
    }
  };

  const handleViewReportClick = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    // navigate to report page, query params with year
    navigate(`/view-report/${id}`);
  };

  const toMeaningfulNumber = (value: number) => {
    const roundedValue = Math.round(value * 100000) / 100000;
    return roundedValue.toLocaleString(formalLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 5,
    });
  };

  const longStatusTemplate = (rowData: Report) => {
    const createdOrModified = rowData.modified
      ? rowData.modified
      : rowData.created;

    if (rowData.reportStatus === ReportStatus.Draft && createdOrModified) {
      return <span>{moment(rowData.created).format("DD.MM.YYYY")}</span>;
    }

    if (rowData.reportStatus === ReportStatus.Sent && createdOrModified) {
      return (
        <span>
          {`${t("report-draft-saved")} ${" "}
          ${moment(createdOrModified).format("DD.MM.YYYY")}`}
        </span>
      );
    }

    return <span></span>;
  };

  const allOrganizationsHaveReported = (
    year: number,
    quarter?: number,
    month?: number,
  ) => {
    if (!organizations || organizations.length === 0) {
      return true;
    }

    const reportsForPeriod = tableData.filter(
      (report) =>
        report.year === year &&
        report.quarter === quarter &&
        report.month === month &&
        report.reportStatus !== ReportStatus.Sent,
    );

    return organizations.every((org) =>
      reportsForPeriod.some((report) => report.organizationId === org.id),
    );
  };

  const headerTemplate = (data: Report) => {
    const allReported = allOrganizationsHaveReported(
      data.year,
      data.quarter,
      data.month,
    );

    const noOrganizations = !organizations || organizations.length === 0;

    const reportsForPeriod = tableData.filter(
      (r) =>
        r.year === data.year &&
        r.quarter === data.quarter &&
        r.month === data.month,
    );

    const allSentForPeriod = reportsForPeriod.every(
      (r) => r.reportStatus === ReportStatus.Sent,
    );

    const readyToSubmit = tableData.every(
      (report) => report.reportStatus === ReportStatus.WaitingForApproval,
    );

    const waitingForReports = tableData.some(
      (report) => report.reportStatus === ReportStatus.Draft,
    );

    return (
      <div
        style={{ display: "flex", justifyContent: "space-between" }}
        className="flex"
      >
        <ReportRowHeaderLeft className="report-group-header-left">
          <div>
            <span style={{ fontWeight: 600 }}>{data.year}</span>
            {data.quarter && <span>{` - Q${data.quarter ?? ""}`}</span>}
          </div>
          {!organizationId &&
            !allReported &&
            !waitingForReports &&
            !adminMode &&
            !noOrganizations &&
            !allSentForPeriod && (
              <div
                style={{
                  color: "var(--sumi-warning-color)",
                  fontSize: "0.9rem",
                }}
              >
                {t("text-pending-reports")}
              </div>
            )}
          {!organizationId &&
            allReported &&
            !readyToSubmit &&
            !adminMode &&
            !noOrganizations && (
              <div
                style={{ color: "var(--sumi-light-green)", fontSize: "0.9rem" }}
              >
                {t("text-all-reports-ready-submitted")}
              </div>
            )}
        </ReportRowHeaderLeft>
        <ReportRowHeaderRight>
          {!organizationId && !adminMode && !noOrganizations && (
            <StyledLink
              onClick={() => {
                navigate(
                  `/summary-report?year=${data.year}&quarter=${data.quarter}&month=${data.month}&status=${data.reportStatus}`,
                );
              }}
            >
              {!noOrganizations && allReported && !readyToSubmit
                ? t("cmd-view-summary-and-send")
                : t("cmd-view-summary")}
            </StyledLink>
          )}
          {adminMode && handleSelectCombinedViewReport && !noOrganizations && (
            <StyledLink
              onClick={() => {
                handleSelectCombinedViewReport(data);
              }}
            >
              {t("cmd-view-summary")}
            </StyledLink>
          )}
        </ReportRowHeaderRight>
      </div>
    );
  };

  const userActionsTemplate = (rowData: Report) => {
    if (rowData.reportStatus === ReportStatus.Sent || adminMode) {
      return <span></span>;
    }

    const canReturn =
      !organizationId &&
      rowData.reportStatus === ReportStatus.WaitingForApproval;

    return (
      <ActionContainer>
        {canReturn && (
          <>
            <Tooltip target={`.user-return-icon-${rowData.id}`} />
            <ReturnIcon
              className={`user-return-icon-${rowData.id} pi pi-reply`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmDialog({
                  showHeader: false,
                  message: t("are-you-sure-you-want-return-report"),
                  acceptLabel: t("cmd-yes"),
                  rejectLabel: t("cmd-cancel"),
                  accept: () => {
                    returnReportForEditing(rowData);
                  },
                  style: { minWidth: "300px" },
                });
              }}
              tabIndex={0}
              role="button"
              data-pr-tooltip={t("cmd-return-to-edit")}
              data-pr-position="left"
            />
          </>
        )}
        {rowData.reportStatus === ReportStatus.Draft && (
          <DeleteIcon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              confirmDialog({
                showHeader: false,
                message: t("are-you-sure-you-want-remove-report"),
                acceptLabel: t("cmd-remove"),
                rejectLabel: t("cmd-cancel"),
                accept: () => {
                  deleteReport(rowData.id);
                },
                style: { minWidth: "300px" },
              });
            }}
            className="pi pi-trash"
            tabIndex={0}
            role="button"
          />
        )}
      </ActionContainer>
    );
  };

  const actionsTemplate = (
    rowData: Report & { internalMessagesCount?: number },
  ) => {
    const displayFlag =
      rowData.internalReportStatus ||
      (rowData.comment && !rowData.internalReportStatus);

    return (
      <ActionContainer>
        {/* Display flag if item needs to be reviewed or if there is a comment and internalReportStatus does not exist */}
        {displayFlag && (
          <>
            <Tooltip target={`.flagged-icon-${rowData.id}`} />
            <FlagIcon
              className={`flagged-icon-${rowData.id} pi pi-flag-fill`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setReportReviewDialog({
                  report: rowData,
                });
              }}
              tabIndex={0}
              role="button"
              $status={
                rowData.internalReportStatus
                  ? rowData.internalReportStatus
                  : rowData.comment
                    ? InternalReportStatusEnum.ToBeReviewed
                    : undefined
              }
              data-pr-tooltip={getInternalStatusText(
                rowData.internalReportStatus
                  ? rowData.internalReportStatus
                  : rowData.comment
                    ? InternalReportStatusEnum.ToBeReviewed
                    : undefined,
              )}
              data-pr-position="top"
              data-pr-at="right-10 top-10"
            />
          </>
        )}
        <Tooltip target={`.chat-icon-${rowData.id}`} />
        <ChatIconContainer>
          <ChatIcon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setReportChatDialog(rowData.id);
            }}
            className={`chat-icon-${rowData.id} pi pi-comments`}
            tabIndex={0}
            role="button"
            data-pr-tooltip={t("cmd-view-internal-comments")}
            data-pr-position="top"
            data-pr-at="right-10 top-10"
          />
          {rowData.internalMessagesCount != null &&
            rowData.internalMessagesCount > 0 && (
              <MessageCountBadge>
                {rowData.internalMessagesCount > 99
                  ? "99+"
                  : rowData.internalMessagesCount}
              </MessageCountBadge>
            )}
        </ChatIconContainer>
        {(rowData.reportStatus === ReportStatus.Sent ||
          rowData.reportStatus === ReportStatus.WaitingForApproval) && (
          <>
            <Tooltip target={`.return-to-edit-icon-${rowData.id}`} />
            <ReturnIcon
              className={`return-to-edit-icon-${rowData.id} pi pi-reply`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmDialog({
                  showHeader: false,
                  message: t("are-you-sure-you-want-return-report"),
                  acceptLabel: t("cmd-yes"),
                  rejectLabel: t("cmd-cancel"),
                  accept: () => {
                    if (returnToEditReport) {
                      returnToEditReport(rowData.id);
                    }
                  },
                  style: { minWidth: "300px" },
                });
              }}
              style={{ marginRight: "10px" }}
              tabIndex={0}
              role="button"
              data-pr-tooltip={t("cmd-return-to-edit")}
              data-pr-position="left"
            />
          </>
        )}
      </ActionContainer>
    );
  };

  return (
    <>
      {reportChatDialog !== 0 && (
        <CommentDialog
          visible={reportChatDialog !== 0}
          onHide={() => setReportChatDialog(0)}
          reportId={reportChatDialog}
        />
      )}

      {reportReviewDialog?.report && (
        <AdminReportReviewDialog
          visible={!!reportReviewDialog}
          onHide={() => setReportReviewDialog(null)}
          report={reportReviewDialog.report}
        />
      )}

      <StyledTable
        dataKey="id"
        tableStyle={{ minWidth: "60rem" }}
        showHeaders={false}
        value={tableData || []}
        lazy
        paginator={showPagination}
        first={(page - 1) * rows}
        rows={rows}
        totalRecords={totalReports}
        onPage={(e) => {
          const newPage = Math.floor(e.first / e.rows) + 1;
          if (newPage !== page && setPage) {
            setPage(newPage);
          }
          if (e.rows !== rows && setRows) {
            setRows(e.rows);
          }
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={`{first} - {last} / ${totalReports}`}
        emptyMessage={t("text-no-reports")}
        rowGroupMode={groupRowsBy ? "subheader" : undefined}
        groupRowsBy={groupRowsBy}
        rowGroupHeaderTemplate={headerTemplate}
        loading={
          isLoadingEcomodClasses || isLoadingSupClasses || isLoadingPrices
        }
      >
        <Column
          field="status"
          style={{ width: "200px" }}
          sortable={!showPagination}
          body={statusbody}
        />
        {showCompanyName && (
          <Column
            field="companyName"
            sortable={!showPagination}
            style={{ width: "250px" }}
          />
        )}
        {(isCombinedOrganizationData || showOrganizationName) && (
          <Column
            field="organizationName"
            sortable={!showPagination}
            style={{ width: "250px" }}
          />
        )}
        <Column
          field="year"
          sortable={!showPagination}
          style={{ width: "70px" }}
        />
        <Column
          field="quarter"
          sortable={!showPagination}
          style={{ width: "70px" }}
          body={quarterTemplate}
        />
        <Column
          field="draftSaved"
          sortable={!showPagination}
          body={longStatusTemplate}
        />
        {showTotalWeight && (
          <Column
            field="totalWeight"
            header={t("label-total-weight")}
            sortable={!showPagination}
            style={{ width: "150px" }}
            body={(rowData: Report & { totalWeight?: number }) => (
              <span>
                {rowData.totalWeight
                  ? toMeaningfulNumber(rowData.totalWeight) + " kg"
                  : ""}
              </span>
            )}
          />
        )}
        {showReportType && (
          <Column
            field="reportType"
            header={t("header-report-type")}
            sortable={!showPagination}
            style={{ width: "150px" }}
            body={(rowData: Report) => {
              switch (rowData.reportType) {
                case ReportType.EAN:
                  return <span>{t("label-report-type-ean")}</span>;
                case ReportType.DETAILED:
                  return <span>{t("label-report-type-detailed")}</span>;
                case ReportType.LIGHT:
                  return <span>{t("label-report-type-light")}</span>;
                default:
                  return <span></span>;
              }
            }}
          />
        )}
        <Column body={downloadReportTemplate} style={{ width: "150px" }} />
        <Column body={viewReportTemplate} style={{ width: "200px" }} />
        <Column
          body={adminMode ? actionsTemplate : userActionsTemplate}
          style={{ width: "100px" }}
        />
      </StyledTable>
    </>
  );
};

export default ReportsTable;
